import { Fragment, useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLazyQuery, gql } from "@apollo/client";
import {
    useReactTable,
    getCoreRowModel,
    getExpandedRowModel,
    type ColumnDef,
    flexRender,
    type Row,
    type ColumnFiltersState,
    getFilteredRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getSortedRowModel,
} from "@tanstack/react-table";
import CmpFilter from "./CmpFilter";
import Loader from "../Loader";
import "./style.css";
import { apolloClient, clearCache, Helper } from "../../../utils";

interface TableProps<TData> {
    headers: Array<ColumnDef<TData>>;
    renderSubComponent: (props: { row: Row<TData> }) => React.ReactElement;
    getRowCanExpand: (row: Row<TData>) => boolean;
    enableColumnFilter?: boolean;
    QUERY: string;
    dataPath: string;
    variables?: Record<string, string>;
    onFilterChange?: (filter: any) => void;
    tableType?: string;
    cacheName?: string;
}

const CmpTable = <TData extends object>(props: TableProps<TData>) => {
    const {
        headers,
        renderSubComponent,
        getRowCanExpand = () => true,
        enableColumnFilter = true,
        variables,
        QUERY,
        dataPath,
        onFilterChange,
        tableType,
        cacheName,
    } = props;

    const currentUser = useSelector((state: any) => state.users.currentUser);

    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

    const columns = [
        ...headers,
        {
            id: "expander",
            header: "Actions",
            cell: ({ row }: { row: Row<TData> }) => {
                return (
                    row.getCanExpand() && (
                        <button
                            type="button"
                            onClick={() => {
                                table.toggleAllRowsExpanded(false);
                                row.toggleExpanded(!row.getIsExpanded());
                            }}
                            className="btn btn btn-primary btn-sm btn--angle text-nowrap"
                        >
                            More Option
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                height="16px"
                                className="ml-1 mt-1"
                                viewBox="0 0 448 512"
                            >
                                <path
                                    d="M201.4 342.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 274.7 86.6 137.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z"
                                    fill="currentColor"
                                />
                            </svg>
                        </button>
                    )
                );
            },
        },
    ];

    const [filter, setFilter]: any = useState(variables?.filter ?? {});

    const [{ pageIndex, pageSize }, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const pagination = useMemo(
        () => ({
            pageIndex,
            pageSize,
        }),
        [pageIndex, pageSize]
    );

    const [tableData, setTableData] = useState([]);

    const [tableInfo, setTableInfo] = useState({
        pageIndex,
        pageSize,
        totalCount: 0,
        pageInfo: {
            hasNextPage: undefined,
            hasPreviousPage: undefined,
            startCursor: undefined,
            endCursor: undefined,
        },
    });

    const pageCount = Math.ceil(tableInfo.totalCount / pageSize);

    const table = useReactTable<TData>({
        data: tableData,
        columns,
        getRowCanExpand,
        pageCount,
        state: {
            columnFilters,
            pagination,
        },
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getSortedRowModel: getSortedRowModel(),
        onPaginationChange: setPagination,
        manualPagination: true,
    });

    const handlePagination = (navigation: string) => {
        navigation === "prev" ? table.previousPage() : table.nextPage();
        table.toggleAllRowsExpanded(false);
    };

    const handleFilter = (column: string, value: string) => {
        table.toggleAllRowsExpanded(false);

        const filterType: string =
            column === "appName" ||
            column === "publishStatus" ||
            column === "status" ||
            column === "isEnabled"
                ? "equalTo"
                : column === "adminPermissions"
                  ? "contains"
                  : column === "country"
                    ? "in"
                    : "includesInsensitive";

        if (column === "Name") {
            let firstName = "";
            let lastName = "";

            const lastIndex = value.lastIndexOf(" ");
            if (lastIndex !== -1) {
                firstName = value.substring(0, lastIndex);
                lastName = value.substring(lastIndex + 1);
            } else {
                firstName = value;
            }

            const newFilter = { ...filter };

            if (firstName !== "") {
                newFilter.firstName = {
                    [filterType]: firstName,
                };
            } else {
                delete newFilter.firstName;
            }

            if (lastName !== "") {
                newFilter.lastName = {
                    [filterType]: lastName,
                };
            } else {
                delete newFilter.lastName;
            }

            setFilter(newFilter);
        } else if (column === "country") {
            if (value === "") {
                if (currentUser.userType === 99) {
                    const { [column]: _, ...rest } = filter;
                    setFilter(rest);
                } else {
                    setFilter({
                        ...filter,
                        [column]: {
                            [tableType === "veevaTable" ? "inInsensitive" : filterType]: [
                                ...currentUser.country,
                            ],
                        },
                    });
                }
            } else {
                if (tableType === "adminTable") {
                    setFilter({
                        ...filter,
                        [column]: {
                            contains: [value],
                        },
                    });
                } else if (tableType === "veevaTable") {
                    setFilter({
                        ...filter,
                        [column]: {
                            includesInsensitive: value,
                        },
                    });
                } else {
                    setFilter({
                        ...filter,
                        [column]: {
                            [filterType]: [value],
                        },
                    });
                }
            }
        } else if (column === "abbvieProGroupSpecialtyByGroupId") {
            if (value === "") {
                setFilter({
                    ...filter,
                    // @ts-expect-error: expected
                    ...variables.filter,
                });
            } else {
                setFilter({
                    ...filter,
                    groupId: {
                        equalTo: value,
                    },
                });
            }
        } else if (column === "configuredApps") {
            if (value === "") {
                const { appName: _, ...rest } = filter;
                setFilter(rest);
            } else {
                setFilter({
                    ...filter,
                    appName: {
                        includesInsensitive: value,
                    },
                });
            }
        } else if (column === "countryName") {
            if (value === "") {
                const { country: _, ...rest } = filter;
                setFilter(rest);
            } else {
                setFilter({
                    ...filter,
                    country: {
                        includesInsensitive: value,
                    },
                });
            }
        } else if (column === "orderNo") {
            if (value === "") {
                const { [column]: _, ...rest } = filter;
                setFilter(rest);
            } else {
                setFilter({
                    ...filter,
                    [column]: {
                        equalTo: Number(value),
                    },
                });
            }
        } else {
            if (value === "") {
                const { [column]: _, ...rest } = filter;
                setFilter(rest);
            } else {
                setFilter({
                    ...filter,
                    [column]: {
                        [filterType]:
                            value === "true"
                                ? true
                                : value === "false"
                                  ? false
                                  : value === "1"
                                    ? Number(value)
                                    : value === "0"
                                      ? Number(value)
                                      : value,
                    },
                });
            }
        }
    };

    const [getData, { loading, data }] = useLazyQuery(gql(QUERY), {
        variables: {
            first: pageSize,
            after: undefined,
            last: undefined,
            before: undefined,
            ...variables,
        },
    });

    useEffect(() => {
        if (data) {
            setTableData(Helper.accessNestedObject(data, `${dataPath}.nodes`));
            const pageInfo = Helper.accessNestedObject(data, `${dataPath}.pageInfo`);

            setTableInfo({
                pageIndex,
                pageSize,
                totalCount: Helper.accessNestedObject(data, `${dataPath}.totalCount`),
                pageInfo: {
                    ...pageInfo,
                },
            });
        }
    }, [data]);

    useEffect(() => {
        let variables: any = {
            first: pageSize,
            after: undefined,
            last: undefined,
            before: undefined,
        };

        if (Object.keys(filter).length > 0) {
            variables = {
                ...variables,
                filter: { ...filter },
            };
        }

        const { startCursor, endCursor } = tableInfo.pageInfo;

        if (pageIndex > tableInfo.pageIndex) {
            variables.first = endCursor ? pageSize : undefined;
            variables.after = endCursor;
        } else if (pageIndex < tableInfo.pageIndex) {
            variables.last = startCursor ? pageSize : undefined;
            variables.before = startCursor;
            variables.first = startCursor ? undefined : pageSize;
        }

        if (onFilterChange) {
            onFilterChange(filter);
        }

        getData({ variables });
    }, [pageIndex, pageSize, filter]);

    useEffect(() => {
        table.toggleAllRowsExpanded(false);
    }, [tableInfo.totalCount]);

    useEffect(() => {
        if (cacheName) {
            const cache = apolloClient.cache;
            clearCache(cache, cacheName);
        }
    }, [filter]);

    return (
        <div className="cmp-table position-relative">
            {loading && <Loader />}

            <div className="table-responsive">
                <table className="table cmp-table__table mb-0">
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <th key={header.id} colSpan={header.colSpan}>
                                            {header.isPlaceholder ? null : (
                                                <>
                                                    <div
                                                        {...{
                                                            className: header.column.getCanSort()
                                                                ? "table-heading cursor-pointer"
                                                                : "",
                                                            onClick:
                                                                header.column.getToggleSortingHandler(),
                                                        }}
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                        {{
                                                            asc: (
                                                                <svg
                                                                    className="ms-2"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    height="1em"
                                                                    width="1em"
                                                                    viewBox="0 0 448 512"
                                                                >
                                                                    <path d="M201.4 137.4c12.5-12.5 32.8-12.5 45.3 0l160 160c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L224 205.3 86.6 342.6c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3l160-160z" />
                                                                </svg>
                                                            ),
                                                            desc: (
                                                                <svg
                                                                    className="ms-2"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    height="1em"
                                                                    viewBox="0 0 448 512"
                                                                >
                                                                    <path d="M201.4 342.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 274.7 86.6 137.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" />
                                                                </svg>
                                                            ),
                                                        }[header.column.getIsSorted() as string] ??
                                                            null}
                                                    </div>

                                                    {header.column.getCanFilter() &&
                                                    enableColumnFilter ? (
                                                        <div className="table-filter">
                                                            <CmpFilter
                                                                columnName={header.column.id}
                                                                handleChange={(value: string) => {
                                                                    handleFilter(
                                                                        header.column.id,
                                                                        value
                                                                    );
                                                                }}
                                                            />
                                                        </div>
                                                    ) : null}
                                                </>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>

                    <tbody>
                        {table.getRowModel().rows.length
                            ? table.getRowModel().rows.map((row) => {
                                  return (
                                      <Fragment key={row.id}>
                                          <tr>
                                              {row.getVisibleCells().map((cell) => {
                                                  return (
                                                      <td key={cell.id}>
                                                          {flexRender(
                                                              cell.column.columnDef.cell,
                                                              cell.getContext()
                                                          )}
                                                      </td>
                                                  );
                                              })}
                                          </tr>
                                          {row.getIsExpanded() && (
                                              <tr>
                                                  <td colSpan={row.getVisibleCells().length}>
                                                      {renderSubComponent({
                                                          row,
                                                      })}
                                                  </td>
                                              </tr>
                                          )}
                                      </Fragment>
                                  );
                              })
                            : !loading && (
                                  <tr>
                                      <td
                                          className="text-danger text-center"
                                          colSpan={table.getAllColumns().length}
                                      >
                                          No Row Found!!!
                                      </td>
                                  </tr>
                              )}
                    </tbody>
                </table>
            </div>

            <div className="cmp-table__pagination border p-1 rounded mt-4">
                <div className="row align-items-center">
                    <div className="col-2">
                        <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={() => {
                                handlePagination("prev");
                            }}
                            disabled={!table.getCanPreviousPage()}
                        >
                            {"<"} Previous
                        </button>
                    </div>
                    <div className="col-8 d-flex align-items-center justify-content-between">
                        <div className="cmp-table__pages">
                            Page{" "}
                            <strong>
                                {table.getPageCount()
                                    ? table.getState().pagination.pageIndex + 1
                                    : 1}{" "}
                                of {table.getPageCount() || 1}
                            </strong>
                        </div>

                        <div className="d-inline-flex align-items-center">
                            Show
                            <select
                                className="form-control mb-0 h-auto p-2 pr-0 ml-2"
                                value={table.getState().pagination.pageSize}
                                onChange={(e) => {
                                    table.setPageSize(Number(e.target.value));
                                }}
                            >
                                {[10, 20, 30, 40, 50].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        {pageSize}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="col-2">
                        <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={() => {
                                handlePagination("next");
                            }}
                            disabled={!table.getCanNextPage()}
                        >
                            Next {">"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CmpTable;
